import { htmlEscape } from "./vanilla";

/* eslint-disable @typescript-eslint/naming-convention */
interface IIndexes {
    "[": number[];
    "]": number[];
    "(": number[];
    ")": number[];
}
/* eslint-enable @typescript-eslint/naming-convention */

interface IPhrase {
    text: string;
    link?: string;
    geotabDriveLink?: boolean;
}

let collectIndexes = (str: string): IIndexes => {
    let indexes = { "[": [], "]": [], "(": [], ")": [] };

    for (let i = 0; i < str.length; i++) {
        let letter = str[i];
        if (indexes[letter]) {
            indexes[letter].push(i);
        }
    }

    return indexes;
};

const supportedSchemes = ["https:", "mailto:", "tel:", "//", "geotabdrive://"];
let isValidLink = (link: string) => {
    let normalizedLink = link.trim().toLowerCase();
    return supportedSchemes.some(scheme => normalizedLink.indexOf(scheme) === 0);
};

let escapeLink = (link: string): string => {
    return link
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
};

let getNearestRoundRightIndex = (startingRoundIndex: number, roundLeftIndexes: number[], roundRightIndexes: number[]) => {
    let countRoundLeftBetween = (startIndex: number, endIndex: number, roundLeftIndexesArr: number[]) => {
        return roundLeftIndexesArr.filter(index => startIndex < index && index < endIndex).length;
    };

    let filteredRoundIndexes = roundRightIndexes.filter(i => i > startingRoundIndex);
    for (let i = 0; i < filteredRoundIndexes.length; i++) {
        let roundRightIndex = filteredRoundIndexes[i];
        let roundLeftBetween = countRoundLeftBetween(startingRoundIndex, roundRightIndex, roundLeftIndexes);

        if (roundLeftBetween === i) {
            return roundRightIndex;
        }
    }

    return Infinity;
};

let getNearestSquareLeftIndex = (endingSquareIndex: number, squareLeftIndexes: number[], squareRightIndexes: number[]) => {
    let countSquareRightBetween = (startIndex: number, endIndex: number, squareRightIndexesArr: number[]) => {
        return squareRightIndexesArr.filter(index => startIndex < index && index < endIndex).length;
    };

    let filteredSquareLeftIndexes = squareLeftIndexes.filter(i => i < endingSquareIndex);
    for (let i = 0; i < filteredSquareLeftIndexes.length; i++) {
        let squareLeftIndex = filteredSquareLeftIndexes[i];
        let squareRightBetween = countSquareRightBetween(squareLeftIndex, endingSquareIndex, squareRightIndexes);

        if (squareRightBetween === i) {
            return squareLeftIndex;
        }
    }

    return -Infinity;
};

export function parseMessage (message: string): IPhrase[] {
    // no symbols in message to create a link
    if (message.indexOf("[") === -1 ||
        message.indexOf("]") === -1 ||
        message.indexOf("(") === -1 ||
        message.indexOf(")") === -1
    ) {
        return [ { text: htmlEscape(message) } ];
    }

    let phrases: IPhrase[] = [];
    let indexes = collectIndexes(message);
    let prevLinkEnd = 0;

    for (let i = 0; i < indexes["]"].length; i++) {
        let squareRightIndex = indexes["]"][i];

        let nearestSquareLeftIndex = getNearestSquareLeftIndex(
            squareRightIndex,
            // eslint-disable-next-line @typescript-eslint/no-loop-func
            indexes["["].filter(ind => ind >= prevLinkEnd).reverse(),
            // eslint-disable-next-line @typescript-eslint/no-loop-func
            indexes["]"].filter(ind => ind >= prevLinkEnd)
        );
        let isRoundLeftNear = indexes["("].indexOf(squareRightIndex + 1) !== -1;
        let nearestRoundRightIndex = getNearestRoundRightIndex(squareRightIndex + 1, indexes["("], indexes[")"]);

        if (nearestSquareLeftIndex !== -Infinity && isRoundLeftNear && nearestRoundRightIndex !== Infinity) {
            if (prevLinkEnd < nearestSquareLeftIndex) {
                phrases.push({
                    text: htmlEscape(message.slice(prevLinkEnd, nearestSquareLeftIndex))
                });
            }

            const link = message.slice(squareRightIndex + 2, nearestRoundRightIndex);
            if (isValidLink(link)) {
                phrases.push({
                    text: htmlEscape(message.slice(nearestSquareLeftIndex + 1, squareRightIndex)),
                    link: encodeURI(escapeLink(link)),
                    geotabDriveLink: link.startsWith("geotabdrive://")
                });
            } else {
                phrases.push({
                    text: htmlEscape(message.slice(nearestSquareLeftIndex, nearestRoundRightIndex + 1))
                });
            }

            prevLinkEnd = nearestRoundRightIndex + 1;
        }
    }

    if (prevLinkEnd < message.length) {
        phrases.push({
            text: htmlEscape(message.slice(prevLinkEnd, message.length))
        });
    }

    return phrases;
}